/**
 * 全应用唯一的 AudioContext：牌桌合成音（sfx.ts）、触觉降级音（haptics.ts）、
 * 录音拟音与短语语音（sampleSfx.ts）都从这里拿同一个。
 *
 * ★ 只在「还没有」或「已经 closed」时才新建。上一版 sfx / haptics 各自持有一个，
 *   并且只要 resume() 被拒就把手里那个扔掉、下一声再 `new AudioContext()`——旧的从不 close。
 *   在 resume 会被拒的环境里，每一声下注 / 过牌都多出一个音频上下文，越打越多
 *   （sfx.test.ts 复现过：上一版连响 30 声建了 30 个）。
 *   不在手势里 resume，浏览器要么拒绝、要么让它一直挂到下一次解锁——都只说明「这一下不是手势」，
 *   等下一次手势再 resume 同一个就行。
 * ★ 一个页面一个 context 也是浏览器的推荐用法：每个 context 都各自占一条音频渲染线程。
 */
import type { AudioContextLike } from './haptics.js'

type AudioContextCtor = new () => AudioContextLike

/** 浏览器默认工厂。拿不到构造器就返回 null，调用方自然退化成「只有视觉补偿」。 */
export function defaultAudioContextFactory(): AudioContextLike | null {
  const g = globalThis as unknown as {
    AudioContext?: AudioContextCtor
    webkitAudioContext?: AudioContextCtor
  }
  const Ctor = g.AudioContext ?? g.webkitAudioContext
  if (Ctor === undefined) return null
  try {
    return new Ctor()
  } catch {
    return null
  }
}

let factory: () => AudioContextLike | null = defaultAudioContextFactory
let shared: AudioContextLike | null = null
/** 构造器不存在 / 构造失败：判定不支持，不再每一声都去试 */
let unsupported = false

/** 拿全应用共用的 AudioContext；已有且没关掉就一直复用同一个。 */
export function sharedAudioContext(): AudioContextLike | null {
  if (shared !== null && shared.state !== 'closed') return shared
  if (unsupported) return null
  shared = factory()
  if (shared === null) unsupported = true
  return shared
}

function isThenable(v: unknown): v is PromiseLike<unknown> {
  return v !== null && typeof v === 'object' && typeof (v as { then?: unknown }).then === 'function'
}

/**
 * 让 context 跑起来。resolve 为「此刻是否已经 running」，**永不 reject**：
 * 不在手势里被拒（或一直挂着不回）是常态，吞掉即可，下一次手势再来。
 *
 * 必须在手势的同步调用栈里调用才可能成功（iOS / Chrome 的自动播放策略）。
 */
export function resumeAudioContext(ctx: AudioContextLike): Promise<boolean> {
  if (ctx.state === 'running') return Promise.resolve(true)
  if (ctx.state === 'closed') return Promise.resolve(false)
  let pending: unknown
  try {
    pending = ctx.resume?.()
  } catch {
    return Promise.resolve(false)
  }
  if (isThenable(pending)) {
    return Promise.resolve(pending).then(
      () => ctx.state === 'running',
      () => false,
    )
  }
  return Promise.resolve(ctx.state === 'running')
}

interface SilentKickContext {
  createBuffer?(channels: number, length: number, sampleRate: number): unknown
  createBufferSource?(): { buffer: unknown; connect(dest: unknown): void; start(when?: number): void }
}

/**
 * 在手势里解锁：先播一段 1 个采样的静音 buffer，再 resume（docs/ARCHITECTURE.md §11.9 的 iOS 解锁流程 [N]）。
 *
 * 老一些的 iOS 只认「手势里真的开播过一个声源」，光 resume 不算；静音 buffer 听不见，
 * 也不占用任何音频元素。已经 running 时什么都不做。必须在手势的同步调用栈里调用。
 */
export function unlockAudioContext(ctx: AudioContextLike): Promise<boolean> {
  if (ctx.state === 'running' || ctx.state === 'closed') return resumeAudioContext(ctx)
  const c = ctx as AudioContextLike & SilentKickContext
  if (typeof c.createBuffer === 'function' && typeof c.createBufferSource === 'function') {
    try {
      const source = c.createBufferSource()
      source.buffer = c.createBuffer(1, 1, 22050)
      source.connect(ctx.destination)
      source.start(0)
    } catch {
      // 拿不到就只靠 resume
    }
  }
  return resumeAudioContext(ctx)
}

interface AudioSessionLike {
  type: string
}

/**
 * iOS / iPadOS 16.4+ 的 `navigator.audioSession`：音效开着时把页面声明成「媒体播放」。
 *
 * ★ 只有 Web Audio 在响时，WebKit 把页面归到「环境音」，**静音拨杆一拨就全没声**。
 *   上一版的拟音和短语走 <audio>，属于「媒体播放」，静音模式下照样响；改走 Web Audio 之后，
 *   背景音乐关着的 iPhone 在静音模式下会整桌无声。声明成 playback 就回到原来的行为，
 *   代价也和原来一样：出声时会打断别的 App 正在放的音乐。
 * ★ 音效关掉时改回 auto：触觉降级的提示音也走这个 context，不该在静音模式下响。
 * 不支持这个接口的浏览器什么都不做。
 */
export function setPlaybackAudioSession(soundOn: boolean): void {
  const session = (globalThis.navigator as { audioSession?: AudioSessionLike } | undefined)?.audioSession
  if (session === undefined) return
  const next = soundOn ? 'playback' : 'auto'
  try {
    if (session.type !== next) session.type = next
  } catch {
    // 个别实现只读或不认这个值：维持原状
  }
}

/** 仅供测试：换工厂并清空共享实例。 */
export function __setAudioContextFactory(next: (() => AudioContextLike | null) | null): void {
  factory = next ?? defaultAudioContextFactory
  shared = null
  unsupported = false
}
